import React from 'react';
import moment from 'moment';
import { useBreakpoint } from 'hooks';
import { CheckboxProps } from 'components/Checkbox';
import { featureEnabled } from 'utils';
import { ShowingsCalendarAppointmentStatus } from '../types';
import ToolBarMobile from './ToolBarMobile';
import ToolBarDesktop from './ToolBarDesktop';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';

type ToolBarProps = {
  isSearchClicked: boolean;
  setIsSearchClicked: React.Dispatch<React.SetStateAction<boolean>>;
};

const ToolBar: React.FC<ToolBarProps> = ({
  isSearchClicked,
  setIsSearchClicked,
}) => {
  const { md } = useBreakpoint();
  const [localStatuses, setLocalStatuses] = React.useState<
    ShowingsCalendarAppointmentStatus[] | undefined
  >();
  const [dateRangeOpen, setDateRangeOpen] = React.useState<boolean>(false);

  const context = useShowingsCalendarContext();
  const { calendarType, setDate, statuses, pendingStatus, date } = context;

  const chronologicalShowingsEnabled = featureEnabled('chronologicalShowings');

  React.useEffect(() => {
    if (pendingStatus) {
      return setLocalStatuses([...statuses, 'pending']);
    }
    setLocalStatuses(statuses);
  }, [statuses]);
  const statusOnChange = React.useCallback(
    (key: ShowingsCalendarAppointmentStatus): CheckboxProps['onChange'] =>
      e => {
        const { checked } = e.target;
        if (checked) {
          setLocalStatuses(prev => (prev ? [...prev, key] : [key]));
        } else {
          setLocalStatuses(prev => {
            const newStatuses = prev.filter(status => status !== key);
            return newStatuses.length === 0 ? [] : newStatuses;
          });
        }
      },
    [],
  );

  const dateRangeOnChange = React.useCallback(
    ([_startDate, _endDate]: [
      _startDate?: moment.Moment,
      _endDate?: moment.Moment,
    ]) => {
      setDate(cur => {
        const [startDate, endDate] = cur;

        const today = moment().startOf('day');
        const sixMonthAgo = moment().subtract(5, 'month');

        if (!_startDate || !_endDate) {
          return chronologicalShowingsEnabled
            ? [today, undefined]
            : [sixMonthAgo, undefined];
        }
        if (
          startDate?.toString() !== _startDate?.toString() ||
          endDate?.toString() !== _endDate?.toString()
        ) {
          return [_startDate, _endDate];
        }
        return cur;
      });
    },
    [setDate],
  );

  const setDefaultDateRange = React.useCallback(() => {
    if (chronologicalShowingsEnabled) {
      const today = moment().startOf('day');
      setDate?.([today, undefined]);
    } else {
      const sixMonthAgo = moment().subtract(5, 'month');
      setDate?.([sixMonthAgo, undefined]);
    }
  }, [chronologicalShowingsEnabled, setDate]);

  React.useEffect(() => {
    setDefaultDateRange();
  }, [setDefaultDateRange]);

  if (calendarType === 'listing' && !md && !chronologicalShowingsEnabled) {
    return null;
  }
  return md ? (
    <ToolBarDesktop
      localStatuses={localStatuses}
      setLocalStatuses={setLocalStatuses}
      statusOnChange={statusOnChange}
      dateRangeOnChange={dateRangeOnChange}
    />
  ) : (
    <ToolBarMobile
      isSearchClicked={isSearchClicked}
      setIsSearchClicked={setIsSearchClicked}
      localStatuses={localStatuses}
      setLocalStatuses={setLocalStatuses}
      statusOnChange={statusOnChange}
      dateRangeOnChange={dateRangeOnChange}
      dateRangeOpen={dateRangeOpen}
      setDateRangeOpen={setDateRangeOpen}
    />
  );
};

export default ToolBar;
